/**
 * Which landing-page language a request resolves to, and how that one language
 * is loaded — **without** pulling the other eight in with it.
 *
 * Same split, and the same reason, as `lib/legalDocs/registry.ts`: the barrel in
 * `./index.ts` imports all nine bundles for tests and for the sitemap, while
 * everything that renders in a browser imports from HERE, where each bundle sits
 * behind a dynamic import and Vite emits one chunk per language.
 *
 * The landing page differs from the legal pages in one way that matters for
 * search: the language rides in the PATH (`/es`), not in a query string. Google
 * treats `?lang=` pages as one URL with parameters unless it is told otherwise,
 * which is survivable for a privacy policy nobody searches for and fatal for the
 * page that has to rank for "bloqueador de apps cristiano".
 */

import {
  CONTENT_LANG_ENDONYM,
  CONTENT_LANGS,
  type ContentLang,
  contentLangOf,
} from "@fazly/shared/contentLang";

import type { MarketingBundle } from "./types";

const LOADERS: Record<ContentLang, () => Promise<MarketingBundle>> = {
  en: () => import("./en").then((m) => m.en),
  es: () => import("./es").then((m) => m.es),
  pt: () => import("./pt").then((m) => m.pt),
  tr: () => import("./tr").then((m) => m.tr),
  de: () => import("./de").then((m) => m.de),
};

export const SITE_LANGS = CONTENT_LANGS;
export const SITE_LANG_ENDONYM = CONTENT_LANG_ENDONYM;
export const DEFAULT_SITE_LANG: ContentLang = "en";

/**
 * The path the landing page lives at for a language.
 *
 * English is the bare root rather than `/en`: it is the default, it is what
 * every existing inbound link points at (the App Store listing's marketing URL,
 * the share links, the QR codes), and giving it a second address would split
 * that authority across two URLs for no gain. The `x-default` hreflang points
 * here too — see `lib/seo.ts`.
 */
export function langPath(lang: ContentLang): string {
  return lang === DEFAULT_SITE_LANG ? "/" : `/${lang}`;
}

/**
 * Turn a URL segment into a language, or `null` if it is not one of ours.
 *
 * Null rather than a fallback on purpose, and this is the one place the landing
 * page is stricter than the legal pages: `/privacy?lang=zz` should still render
 * a policy, but `/zz` is a URL that does not exist, and answering it with a
 * translated home page would hand Google an unbounded supply of duplicate pages
 * — one per typo anyone ever links.
 */
export function siteLangOf(segment: string | undefined): ContentLang | null {
  if (!segment) return null;
  return contentLangOf(segment);
}

/** Load one language's landing copy. */
export function loadMarketingBundle(
  lang: ContentLang,
): Promise<MarketingBundle> {
  return LOADERS[lang]();
}

export type { ContentLang, MarketingBundle };
